import React from 'react'
import { useQuery } from '@tanstack/react-query'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { getLiveEventsForAllSports } from '@mobi/sports-offering/helpers/api/events'
import { sportsOfferingQueryKeys } from '@mobi/sports-offering/helpers/queries/keys'
import { CenterContentStyled as Center } from '@core/Areas/Account/Pages/Activity/Components/Main/AccountActivityMain.styles'
import { DateHeader } from '@core/Areas/Account/Pages/Activity/Components/PagesDateHeader'
import { Ticket } from '@core/Areas/Account/Pages/Activity/Components/Ticket'
import {
  getIdFromSortedString,
  isFauxTicketTransaction,
} from '@core/Areas/Account/Pages/Activity/helpers'
import type { TicketResponseData } from '@core/Areas/Account/Pages/Activity/types'
import { Coupon } from '@core/Areas/KambiSports/Player/Components/Coupon'
import { isKambiHistoryCoupon } from '@core/Areas/KambiSports/Player/helpers/typeGuard'

// Main export to render collections of pages
export const Pages: React.FC<{
  pages: (TicketResponseData | KambiHistoryCoupon)[]
  isOrderByEvent: boolean
  shouldShowLiveScores?: boolean
}> = ({ pages, isOrderByEvent, shouldShowLiveScores = false }) => {
  const latestDateRef = React.useRef<string | null>(null)

  const { data: liveEventsData } = useQuery({
    queryFn: getLiveEventsForAllSports,
    queryKey: sportsOfferingQueryKeys.LiveEventsForAllSports,
    enabled: shouldShowLiveScores,
    refetchOnWindowFocus: false,
  })

  return pages.map((page, i) => {
    return (
      <Page
        key={isKambiHistoryCoupon(page) ? page.couponRef : page.pageNumber}
        page={page}
        isOrderByEvent={isOrderByEvent}
        latestDateRef={latestDateRef}
        isFirstPage={i === 0}
        liveEvents={liveEventsData?.events}
      />
    )
  })
}

// ===============
// local component
// ===============

// Single page to ensure best performance
const Page = React.memo<{
  page: TicketResponseData | KambiHistoryCoupon
  isOrderByEvent: boolean
  latestDateRef: React.MutableRefObject<string | null>
  isFirstPage: boolean
  liveEvents?: Awaited<ReturnType<typeof getLiveEventsForAllSports>>['events']
}>(({ page, isOrderByEvent, latestDateRef, isFirstPage, liveEvents }) => {
  if (isKambiHistoryCoupon(page)) {
    const liveEvent =
      page.events.length === 1
        ? liveEvents?.find(({ event }) => event.id === page.events[0].eventId)
        : undefined

    const earliestStartDate = page.events.reduce(
      (earliest, event) => (event.eventStartDate < earliest ? event.eventStartDate : earliest),
      page.events[0].eventStartDate
    )

    return [
      <DateHeader
        key={'date.' + earliestStartDate}
        isOrderByEvent={isOrderByEvent}
        eventStartDateTime={earliestStartDate}
        timestamp={earliestStartDate}
        latestDateRef={{ current: null }}
        isFirstDate={false}
      />,
      <Coupon liveEvent={liveEvent} key={page.couponRef} historyCoupon={page} />,
    ]
  }

  return page.sortedIds.map((ticketId, i) => {
    const item = page.ticketsById[getIdFromSortedString(ticketId)]

    if (isFauxTicketTransaction(item))
      return (
        <Center key={ticketId}>
          <strong data-testid='transactions-faux-item'>{item.message}</strong>
        </Center>
      )

    return [
      <DateHeader
        key={'date.' + ticketId}
        isOrderByEvent={isOrderByEvent}
        eventStartDateTime={item.eventStartDateTime}
        timestamp={item.timestamp}
        latestDateRef={latestDateRef}
        isFirstDate={isFirstPage && i === 0}
      />,
      <Ticket key={ticketId} ticket={item} />,
    ]
  })
})

Page.displayName = 'Page'
